import { ActionIcon, Badge, Tooltip, useComputedColorScheme } from '@mantine/core';
import { IconAlertSquareRounded, IconArrowLeft, IconArrowRight, IconMaximize } from '@tabler/icons-react';
import { useEffect, useMemo } from 'react';
import { prettyDuration } from '~/common/time/duration';
import { useQueryParam } from '~/hooks/nav/useQueryParam';
import { useSwitcher } from '~/hooks/state/useSwitcher';
import type { GetTestRunAttemptType } from '~/server/api/routers/testRun';
import { screenshotDiffAtom } from '~/atoms/atoms';
import { useResetAtom } from 'jotai/utils';
import type { RunStatus } from '@prisma/client';
import type { TestRunTraceIdentifier } from '~/server/api/routers/trace';
import { getTraceUrl } from '~/common/trace/getTraceUrl';
import { api } from '~/trpc-extra/api';

const TEST_RUN_STATUS_COLORS: Record<RunStatus, string> = {
  PASSED: 'green',
  FLAKY: 'green',
  FAILED: 'red',
  RUNNING: 'yellow',
  RETRYING: 'yellow',
  ERROR: 'yellow',
  SKIPPED: 'gray',
  QUEUED: 'gray',
  CANCELLED: 'gray',
};

export default function TestRunAttemptSwitcher({
  testRunAttempts,
  isFullView,
  onAttemptChange,
  runStatus,
  traceIdentifier,
}: {
  testRunAttempts: GetTestRunAttemptType[];
  isFullView?: boolean;
  onAttemptChange?: (attempt: GetTestRunAttemptType | undefined) => void;
  runStatus?: RunStatus;
  traceIdentifier?: TestRunTraceIdentifier;
}) {
  // TestRunAttempts already come in descending order (newest first) from the server
  const [attemptId, setAttemptId] = useQueryParam('attemptId');
  const resetScreenshotDiff = useResetAtom(screenshotDiffAtom);
  const colorScheme = useComputedColorScheme();

  // Fetch presigned URL for the trace to construct the external link
  const { data: presignedData } = api.trace.getPresignedUrl.useQuery(
    {
      traceIdentifier: traceIdentifier ?? {
        projectId: '',
        testId: '',
        testRunId: '',
        attemptId: '',
      },
    },
    {
      enabled: !!traceIdentifier,
      refetchOnWindowFocus: false,
    },
  );

  const traceViewerUrl =
    presignedData?.presignedUrl ?
      getTraceUrl(
        { traceZipUrl: presignedData.presignedUrl },
        { theme: colorScheme === 'light' ? 'light-mode' : 'dark-mode' },
      )
    : undefined;

  // Default to the first attempt (most recent) in the array
  const defaultIndex = 0;
  const foundIndex = useMemo(
    () =>
      attemptId !== null ? testRunAttempts.findIndex((attempt: GetTestRunAttemptType) => attempt.id === attemptId) : -1,
    [attemptId, testRunAttempts],
  );
  const initialIndex = foundIndex !== -1 ? foundIndex : defaultIndex;

  const { switcherIndex, switcherItemCount, increment, decrement } = useSwitcher(
    testRunAttempts.length || 0,
    initialIndex,
  );

  const selectedTestRunAttempt = testRunAttempts.at(switcherIndex);

  useEffect(() => {
    if (selectedTestRunAttempt?.id && isFullView) {
      setAttemptId(selectedTestRunAttempt.id);
    }
  }, [selectedTestRunAttempt?.id, isFullView, setAttemptId]);

  useEffect(() => {
    onAttemptChange?.(selectedTestRunAttempt);
    resetScreenshotDiff();
  }, [selectedTestRunAttempt, onAttemptChange]);

  if (testRunAttempts.length === 0) {
    return null;
  }

  // For test run attempts: index 0 = newest, index (length-1) = oldest
  // But from user perspective: newest should allow going "back" to older attempts
  const isAtNewest = switcherIndex === 0;
  const isAtOldest = switcherIndex === switcherItemCount - 1;

  // When the test has the SKIPPED, QUEUED, or CANCELLED runStatus, it may not have any attempts.
  // In these cases, we should show the icon according to the runStatus.
  const status =
    runStatus === 'SKIPPED' || runStatus === 'QUEUED' || runStatus === 'CANCELLED' ?
      runStatus
    : selectedTestRunAttempt?.status;

  return (
    <div className="flex items-start space-x-6 pl-2">
      <div className="flex w-full flex-col items-start space-y-1">
        <div className="flex w-full flex-row items-center gap-4">
          <div className="flex items-center space-x-2">
            {testRunAttempts.length > 1 && (
              <div className="mr-2 flex space-x-1">
                <Tooltip
                  label="Already at the oldest attempt"
                  disabled={!isAtOldest}
                >
                  <ActionIcon
                    title="Previous (Older)"
                    variant="default"
                    size="sm"
                    disabled={isAtOldest}
                    onClick={increment}
                  >
                    <IconArrowLeft
                      style={{ width: '60%', height: '60%' }}
                      stroke={1.5}
                    />
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  label="Already at the newest attempt"
                  disabled={!isAtNewest}
                >
                  <ActionIcon
                    title="Next (Newer)"
                    variant="default"
                    size="sm"
                    disabled={isAtNewest}
                    onClick={decrement}
                  >
                    <IconArrowRight
                      style={{ width: '60%', height: '60%' }}
                      stroke={1.5}
                    />
                  </ActionIcon>
                </Tooltip>
              </div>
            )}
            <Tooltip
              disabled={!selectedTestRunAttempt?.id || !isFullView}
              label={`AttemptId: ${selectedTestRunAttempt?.id}`}
            >
              <span className="text-sm font-medium text-gray-800 dark:text-stone-200">
                Attempt #{Math.max(1, testRunAttempts.length - switcherIndex)}
              </span>
            </Tooltip>
            <Badge
              variant="light"
              color={status ? TEST_RUN_STATUS_COLORS[status] : 'gray'}
            >
              {status}
            </Badge>
            {!!selectedTestRunAttempt?.isRerun && (
              <Badge
                color="blue"
                variant="light"
              >
                RERUN
              </Badge>
            )}
          </div>
          {traceViewerUrl != null && (
            <Tooltip label="Open trace in fullscreen">
              <ActionIcon
                component="a"
                href={traceViewerUrl}
                target="_blank"
                variant="subtle"
                size="sm"
                color="gray"
                className="ml-auto"
              >
                <IconMaximize
                  style={{ width: '85%', height: '85%' }}
                  stroke={2}
                />
              </ActionIcon>
            </Tooltip>
          )}
          {selectedTestRunAttempt?.AttemptError?.type === 'TIMEOUT' && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <IconAlertSquareRounded size={14} />
              This test run exceeded the maximum allowed time. Increase the test timeout in the test suite settings.
            </div>
          )}
        </div>

        <div className="flex gap-3 text-sm text-[var(--mantine-color-dimmed)]">
          {selectedTestRunAttempt?.createdAt.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })}
          <div>Duration: {prettyDuration(Math.ceil((selectedTestRunAttempt?.durationMs ?? 0) / 1000))}</div>
        </div>
      </div>
    </div>
  );
}
